import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Save } from "lucide-react";
import { toast } from "../../components/ui/sonner";

export function FieldRequirementsTab() {
  const FIELD_LABELS = {
    customer_name: "Customer Name",
    quote_number: "Quote Number",
    job_type: "Job Type",
    category: "Category",
    estimator: "Estimator",
    branch: "Labour Type",
    postcode: "Postcode",
    description: "Brief description of works",
    repair_parts: "Parts required (Repair only)",
  };

  const [required, setRequired] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/field-requirements").then((r) => setRequired(r.data?.required || {}));
  }, []);

  const toggle = (k) => setRequired((cur) => ({ ...cur, [k]: !cur[k] }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/field-requirements", { required });
      setRequired(data.required || {});
      setSavedAt(new Date());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!required) return <div className="text-sm text-slate-500">Loading…</div>;

  // Show known labels first, then any future/extra keys
  const knownKeys = Object.keys(FIELD_LABELS);
  const extraKeys = Object.keys(required).filter((k) => !knownKeys.includes(k));
  const allKeys = [...knownKeys, ...extraKeys];

  return (
    <div className="space-y-4" data-testid="field-requirements-tab">
      <div className="text-xs text-slate-500 max-w-2xl">
        Toggle which Quote Builder header fields are required before a quote can be saved. Required fields
        show a red asterisk in the builder and the save action blocks until they are filled in.
      </div>
      <div className="border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-900">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Field</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600 w-32">Required?</th>
            </tr>
          </thead>
          <tbody>
            {allKeys.map((k) => (
              <tr key={k} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{FIELD_LABELS[k] || k}</td>
                <td className="px-3 py-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!required[k]}
                      onChange={() => toggle(k)}
                      className="w-4 h-4 accent-brand-blue"
                      data-testid={`required-toggle-${k}`}
                    />
                    <span className={`text-xs uppercase tracking-wider font-semibold ${required[k] ? "text-red-600" : "text-slate-400"}`}>
                      {required[k] ? "Required" : "Optional"}
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark disabled:opacity-60"
          data-testid="save-field-requirements-button"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
        {savedAt && <span className="text-xs text-slate-500">Saved · {savedAt.toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}
